import { Box, CardContent, Typography, Divider, List, ListItemIcon, ListItem, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { SelectableCard } from '@dhl/components'
import { Check } from '@dhl/icons'

import { selectors } from '~/redux/createRootReducer'
import { Price } from '~/basket/model/utils/price'
import { formattedTotalPrice } from '~/basket/view/util/price'

import { CarrierIcon } from './CarrierIcon'

export type Carrier = {
  key: string
  price: Price
  transitDays: number | null
}

type Props = {
  onClick: () => void
  isSelected: boolean
  showDescription: boolean
  carrier: Carrier
}

export const CarrierCard = ({ carrier, onClick, isSelected, showDescription }: Props) => {
  const { t } = useTranslation()
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))

  const basket = useSelector(selectors.getBasket)
  const translationKey = carrier.key.replace('DHL-', '').toLowerCase()
  const price = formattedTotalPrice(carrier.price)

  const destinationCountryCode = basket?.package?.destinationCountry || 'NL'
  const destinationCountry = t(`inputs.destinationInput.country_${destinationCountryCode}`)
  const towards = destinationCountryCode === 'NL' ? t('within') : t('towards')

  return (
    <SelectableCard isSelected={isSelected} onClick={onClick} data-test={carrier.key}>
      <CardContent
        sx={{
          textAlign: 'center',
          padding: (theme) => theme.spacing(2),
        }}
      >
        <Box mb={2}>
          <Typography variant="h5">{t(`carrier_${translationKey}_title`)}</Typography>
        </Box>
        <Divider />
        <Box>
          <CarrierIcon carrierKey={carrier.key} />
        </Box>
        <Box mb={1}>
          <Typography variant="h5">
            {t('from')} {price}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2">
            {/* eslint-disable-next-line no-nested-ternary */}
            {carrier.transitDays && carrier.transitDays > 1
              ? t('deliverydays_plural', { count: carrier.transitDays })
              : carrier.transitDays && carrier.transitDays > 0
                ? t('deliverydays', { count: carrier.transitDays })
                : ''}
          </Typography>
        </Box>

        {isDesktop && (
          <Box display="flex" alignItems="flex-start">
            {showDescription && (
              <List
                sx={{
                  marginBottom: (theme) => theme.spacing(-2),
                }}
              >
                {[0, 1, 2].map((i) => (
                  <ListItem
                    key={`carrier_description_${i}`}
                    sx={{
                      '&:first-letter': {
                        textTransform: 'capitalize',
                      },
                      padding: 0,
                    }}
                  >
                    <Box m={1} fontSize={6}>
                      <ListItemIcon
                        sx={{
                          display: 'inline',
                        }}
                      >
                        <Check htmlColor="green" />
                      </ListItemIcon>
                    </Box>
                    {t(`carrier_${translationKey}_line${i + 1}`, { price, towards, destinationCountry })}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </CardContent>
    </SelectableCard>
  )
}
